import { screenShareFromJSON, ScreenShareRecord } from "common/records/ScreenShare";
import { RootActions, RootState } from "common/reducers";
import { rootKey } from "common/selectors/screenShare.selectors";
import { Record, Map } from "immutable";
import { Nullable } from "common/types";
import {
  FETCH_SCREEN_SHARE,
  RECEIVE_ROOM_STATUS,
  RECEIVE_SCREEN_SHARE,
  RECEIVE_SCREEN_SHARE_TOKEN,
  SET_ACTIVE_SCREEN_SHARE_JOB,
  SET_LANDING_LOADED,
  SET_SCREEN_SHARE_KIND,
  SET_SCREEN_SHARE_LOADED,
  SET_SCREEN_SHARE_STATUS,
} from "common/reducers/screenShare.actions";

export type ScreenShareStatusValues = "off" | "start_requested" | "stop_requested" | "tab_opened" | "active";
export type ScreenShareKindValues = "premium";

export interface IJobScreenShareRecord {
  screenShare: Nullable<ScreenShareRecord>;
  screenShareStatus: ScreenShareStatusValues;
  screenShareKind: ScreenShareKindValues;
}

export const JobScreenShareRecord: Record.Factory<IJobScreenShareRecord> = Record<IJobScreenShareRecord>({
  screenShare: null,
  screenShareStatus: "off",
  screenShareKind: "premium",
});

export const ScreenShareStateRecord = Record({
  screenShareToken: null,
  activeScreenShareJobId: null,
  byJobId: Map<number, typeof JobScreenShareRecord>(),
  roomExists: false,
  landingLoaded: false,
});

export const initialState = ScreenShareStateRecord();
export type ScreenShareState = typeof initialState;

export const reducer = (state: RootState, action: RootActions): RootState => {
  switch (action.type) {
    case SET_SCREEN_SHARE_STATUS:
      if (!state.getIn([rootKey, "byJobId", action.payload.jobId])) {
        state = state.setIn([rootKey, "byJobId", action.payload.jobId], JobScreenShareRecord());
      }
      return state.setIn([rootKey, "byJobId", action.payload.jobId, "screenShareStatus"], action.payload.status);
    case SET_SCREEN_SHARE_KIND:
      if (!state.getIn([rootKey, "byJobId", action.payload.jobId])) {
        state = state.setIn([rootKey, "byJobId", action.payload.jobId], JobScreenShareRecord());
      }
      return state.setIn([rootKey, "byJobId", action.payload.jobId, "screenShareKind"], action.payload.kind);
    case SET_ACTIVE_SCREEN_SHARE_JOB:
      return state.setIn([rootKey, "activeScreenShareJobId"], action.payload);
    case FETCH_SCREEN_SHARE:
      if (!state.getIn([rootKey, "byJobId", action.payload.jobId])) {
        state = state.setIn([rootKey, "byJobId", action.payload.jobId], JobScreenShareRecord());
      }
      if (state.getIn([rootKey, "byJobId", action.payload.jobId, "screenShare"])) {
        return state.setIn([rootKey, "byJobId", action.payload.jobId, "screenShare", "loading"], true);
      }
      return state.setIn(
        [rootKey, "byJobId", action.payload.jobId, "screenShare"],
        screenShareFromJSON({
          id: action.payload.id,
          job_id: action.payload.jobId,
          loading: true,
        }),
      );
    case RECEIVE_SCREEN_SHARE:
      const screenShare = screenShareFromJSON({ ...action.payload.screenShare, loading: false });

      if (!state.getIn([rootKey, "byJobId", action.payload.jobId])) {
        state = state.setIn([rootKey, "byJobId", action.payload.jobId], JobScreenShareRecord());
      }
      return state.setIn([rootKey, "byJobId", action.payload.jobId, "screenShare"], screenShare);
    case SET_SCREEN_SHARE_LOADED:
      return state.setIn([rootKey, "byJobId", action.payload.jobId, "screenShare", "loading"], false);
    case RECEIVE_SCREEN_SHARE_TOKEN:
      return state.setIn([rootKey, "screenShareToken"], action.payload);
    case RECEIVE_ROOM_STATUS:
      return state.setIn([rootKey, "roomExists"], action.payload);
    case SET_LANDING_LOADED:
      return state.setIn([rootKey, "landingLoaded"], action.payload.loaded);
    default:
      return state;
  }
};
